import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { FormSheet } from "@/components/ui/FormSheet";
import { LocationCropChips, type EleccionCultivo } from "@/components/pests/LocationCropChips";
import type { ScanLocation } from "@/hooks/useScanCropChoices";

/**
 * EL ESCÁNER PREGUNTA «¿DÓNDE Y QUÉ CULTIVO?» ANTES DE ANALIZAR (plan cultivos SIEX F7,
 * 05-10-2026). La foto del escáner (la del asistente: el escáner de «Funciones avanzadas»
 * es hoy esa misma conversación) no viene de un sector; con dos toques la IA sabe el
 * cultivo y compara con SUS plagas. Lo elegido viene ya marcado (lo último usado, o la única
 * ubicación y su único cultivo), así que casi siempre es un toque: «Analizar».
 *
 * Cerrar con la X NO cancela: analiza igual, sin cultivo («No lo sé»): la foto ya está en
 * la conversación y quedarse sin respuesta sería peor. Patrones de `patrones-ui`: FormSheet
 * con la X arriba a la derecha (U1/U2).
 */
export function ScanCropSheet({
  open,
  locations,
  initial,
  onDone,
}: {
  open: boolean;
  locations: ScanLocation[];
  initial: EleccionCultivo;
  /** Se llama UNA vez: con lo elegido al pulsar «Analizar», o con «No lo sé» al cerrar. */
  onDone: (v: EleccionCultivo) => void;
}) {
  const { t } = useTranslation();
  const [valor, setValor] = useState<EleccionCultivo>(initial);
  useEffect(() => {
    if (open) setValor(initial);
  }, [open, initial]);
  return (
    <FormSheet
      open={open}
      onOpenChange={(abierta) => {
        if (!abierta) onDone({ locationId: valor.locationId, cropCode: null });
      }}
      title={t("advanced.cropChips.title", "¿Dónde y qué cultivo?")}
      titleIcon={<Sparkles className="h-5 w-5 text-[#7c5ccf]" aria-hidden />}
      fit="short"
      footer={
        <button
          type="button"
          onClick={() => onDone(valor)}
          data-testid="scan-crop-analyze"
          className="flex h-[52px] w-full items-center justify-center gap-2 rounded-[14px] bg-[#2E84DE] font-['Poppins'] text-base font-bold text-white shadow-[0_8px_18px_rgba(46,132,222,0.30)] transition-transform active:scale-[0.99]"
        >
          {t("advanced.analyzeImage", "Analizar imagen")}
        </button>
      }
    >
      <div data-testid="scan-crop-sheet">
        <LocationCropChips locations={locations} value={valor} onChange={setValor} showTitle={false} className="border-0 bg-transparent p-0" />
      </div>
    </FormSheet>
  );
}

export default ScanCropSheet;
